import React, { useEffect, useRef, createContext, useContext } from "react";
import { Platform } from "react-native";
import { CopilotProvider, useCopilot } from "react-native-copilot";
import { useTheme } from "styled-components/native";
import { useIsFocused } from "@react-navigation/native";
import { SafeAreaInsetsContext } from "react-native-safe-area-context";
import {
  CoachOverlayService,
  CoachOverlayScreen,
} from "@services/CoachOverlayService";
import { useOverlayConfigWeb } from "@redux/slices/globalSlice";
import { JoyrideProvider, useJoyride } from "@components/atoms/Joyride";
import { OverlayConfig } from "@components/atoms/Joyride/utils";
import CustomTooltip from "@components/atoms/CustomToolTip";

interface CoachOverlayContextValue {
  isInitialized: boolean;
}

const CoachOverlayContext = createContext<CoachOverlayContextValue>({
  isInitialized: false,
});

export const useCoachOverlayContext = () => useContext(CoachOverlayContext);

interface WebRendererBridgeProps {
  children: React.ReactNode;
}

const WebRendererBridge: React.FC<WebRendererBridgeProps> = ({ children }) => {
  const joyride = useJoyride() as {
    startTourForScope: (screen: string) => void;
    resetScopeForScope: (screen: string) => void;
  } | null;
  const registeredRef = useRef(false);

  useEffect(() => {
    if (joyride && !registeredRef.current) {
      CoachOverlayService.registerWebRenderer({
        startTourForScope: joyride.startTourForScope,
        resetScopeForScope: joyride.resetScopeForScope,
      });
      registeredRef.current = true;
    }

    return () => {
      CoachOverlayService.unregisterWebRenderer();
      registeredRef.current = false;
    };
  }, [joyride]);

  return <>{children}</>;
};

interface CoachOverlayProviderProps {
  children: React.ReactNode;
}

export const CoachOverlayProvider: React.FC<CoachOverlayProviderProps> = ({
  children,
}) => {
  const theme = useTheme();
  const getOverlayConfigWeb = useOverlayConfigWeb();
  const steps = OverlayConfig(getOverlayConfigWeb?.screens);
  const isWeb = Platform.OS === "web";
  const isLargeScreen = theme.isLargeScreen;

  useEffect(() => {
    CoachOverlayService.initialize();
  }, []);

  const contextValue: CoachOverlayContextValue = {
    isInitialized: true,
  };

  if (isLargeScreen) {
    return (
      <CoachOverlayContext.Provider value={contextValue}>
        <JoyrideProvider steps={steps} disableScrolling={true}>
          <WebRendererBridge>{children}</WebRendererBridge>
        </JoyrideProvider>
      </CoachOverlayContext.Provider>
    );
  }

  if (isWeb) {
    return <>{children}</>;
  }

  return (
    <CoachOverlayContext.Provider value={contextValue}>
      {children}
    </CoachOverlayContext.Provider>
  );
};

interface ScopedCopilotProviderProps {
  children: React.ReactNode;
  scope: CoachOverlayScreen;
}

export const ScopedCopilotProvider: React.FC<ScopedCopilotProviderProps> = ({
  children,
  scope,
}) => {
  const theme = useTheme();
  const isWeb = Platform.OS === "web";
  const isLargeScreen = theme.isLargeScreen;

  if (isWeb || isLargeScreen) {
    return <>{children}</>;
  }

  return (
    <ScopedNativeCopilotProvider scope={scope}>
      {children}
    </ScopedNativeCopilotProvider>
  );
};

const ScopedNativeCopilotProvider: React.FC<ScopedCopilotProviderProps> = ({
  children,
  scope,
}) => {
  const theme = useTheme();
  const insets = useContext(SafeAreaInsetsContext);
  const topInset = insets?.top ?? 0;
  const androidVerticalOffset = Math.max(0, 38 - topInset);

  return (
    <CopilotProvider
      tooltipComponent={(props) => <CustomTooltip {...props} />}
      overlay="svg"
      tooltipStyle={{
        borderRadius: 8,
        backgroundColor: theme["core-01"],
        borderColor: theme["over-04"],
        borderWidth: 2,
      }}
      arrowColor={theme["over-04"]}
      backdropColor={theme["over-01"]}
      stepNumberComponent={() => <></>}
      verticalOffset={
        Platform.OS === "android" ? androidVerticalOffset : undefined
      }
    >
      <ScopedMobileRendererBridge scope={scope}>
        {children}
      </ScopedMobileRendererBridge>
    </CopilotProvider>
  );
};

interface ScopedMobileRendererBridgeProps {
  children: React.ReactNode;
  scope: CoachOverlayScreen;
}

const ScopedMobileRendererBridge: React.FC<ScopedMobileRendererBridgeProps> = ({
  children,
  scope,
}) => {
  const { start, stop, visible } = useCopilot();
  const isFocused = useIsFocused();
  const isMountedRef = useRef(true);
  const prevFocusedRef = useRef(isFocused);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const startRef = useRef(start);
  const stopRef = useRef(stop);

  useEffect(() => {
    startRef.current = start;
    stopRef.current = stop;
  }, [start, stop]);

  useEffect(() => {
    const wrappedStart = (scrollView?: any) => {
      try {
        const supportsScrollTo = typeof scrollView?.scrollTo === "function";
        startRef.current(undefined, supportsScrollTo ? scrollView : undefined);
      } catch (e) {
        // Start may fail
      }
    };

    const wrappedStop = () => {
      try {
        stopRef.current();
      } catch (e) {
        // Stop may fail
      }
    };

    CoachOverlayService.registerScopedMobileRenderer(scope, {
      start: wrappedStart,
      stop: wrappedStop,
    });

    return () => {
      if (CoachOverlayService.getActiveTour() === scope) {
        try {
          stopRef.current();
        } catch (e) {
          // Ignore
        }
        CoachOverlayService.cancelTour();
      }
      CoachOverlayService.unregisterScopedMobileRenderer(scope);
    };
  }, [scope]);

  useEffect(() => {
    const justLostFocus = !isFocused && prevFocusedRef.current;
    prevFocusedRef.current = isFocused;

    if (justLostFocus) {
      if (CoachOverlayService.getActiveTour() === scope) {
        try {
          stop();
        } catch (e) {
          // Ignore
        }
        CoachOverlayService.cancelTour();
      }
    }
  }, [isFocused, scope, stop]);

  useEffect(() => {
    if (!visible && CoachOverlayService.getActiveTour() === scope) {
      CoachOverlayService.cancelTour();
    }
  }, [visible, scope]);

  return <>{children}</>;
};

export default CoachOverlayProvider;
